import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import PersonOffIcon from "@mui/icons-material/PersonOff";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DATABASE } from "../../../Config/firebase";
import CustomSearch from "../../../Components/CustomSearch/CustomSearch";
import TableContainer from "../../../Components/TableContainer/TableContainer";
import useDebounce from "../../../Components/useDebounce/useDebounce";
import {
  CRYPTO_ASSETS,
  CRYPTO_NETWORKS,
  inferWalletProviderFromLabel,
  PROVIDER_COLORS,
  maskAddress,
  MAX_ACTIVE_WALLETS_PER_USER,
  WALLET_TYPES,
  isLegacySecurityWallet,
  validateWalletAddress,
} from "../../../Utils/cryptoWalletUtils";
import { normalizeAddressForCompare } from "../../../Utils/walletQr";
import AddWalletModal from "./components/AddWalletModal";
import EditWalletModal from "./components/EditWalletModal";
import moment from "moment";

const FILTER_SELECT_SX = {
  height: "46px",
  borderRadius: "8px",
  backgroundColor: "rgba(255,255,255,0.07)",
  color: "white",
  "& .MuiSelect-icon": { color: "rgba(255,255,255,0.6)" },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.2)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.5)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.7)",
  },
};

const LABEL_SX = { color: "rgba(255,255,255,0.7)", "&.Mui-focused": { color: "white" } };

const MENU_PROPS = {
  PaperProps: {
    sx: {
      bgcolor: "#1e1e2e",
      color: "white",
      "& .MuiMenuItem-root:hover": { bgcolor: "rgba(42,245,152,0.15)" },
      "& .MuiMenuItem-root.Mui-selected": { bgcolor: "rgba(42,245,152,0.2)" },
    },
  },
};

const assetOptions  = [{ value: "", label: "All Assets" },  ...CRYPTO_ASSETS.map((a) => ({ value: a, label: a }))];
const networkOptions = [{ value: "", label: "All Networks" }, ...CRYPTO_NETWORKS.map((n) => ({ value: n, label: n }))];
const statusOptions  = [{ value: "", label: "All Status" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }];
const typeOptions    = [
  { value: "", label: "All Types" },
  { value: WALLET_TYPES.WITHDRAWAL, label: "Withdrawal" },
  { value: WALLET_TYPES.DEPOSIT,    label: "Deposit" },
];
const securityOptions = [
  { value: "", label: "All Security" },
  { value: "legacy", label: "Legacy only" },
  { value: "ok", label: "Security OK" },
];

const ASSET_COLORS = {
  USDT: { bg: "rgba(38,161,123,0.18)", text: "#2af598", border: "rgba(42,245,152,0.35)" },
  USDC: { bg: "rgba(39,117,202,0.18)", text: "#7eb8ff", border: "rgba(126,184,255,0.35)" },
  BTC:  { bg: "rgba(247,147,26,0.15)", text: "#f7931a", border: "rgba(247,147,26,0.4)" },
};

const NETWORK_COLORS = {
  POLYGON:  { bg: "rgba(130,71,229,0.18)", text: "#b47cff", border: "rgba(180,124,255,0.4)" },
  BSC:      { bg: "rgba(243,186,47,0.15)", text: "#f3ba2f", border: "rgba(243,186,47,0.4)" },
  ETHEREUM: { bg: "rgba(98,126,234,0.18)", text: "#8fa8ff", border: "rgba(143,168,255,0.4)" },
  TRON:     { bg: "rgba(235,0,41,0.15)",   text: "#ff5c70", border: "rgba(255,92,112,0.4)" },
  BITCOIN:  { bg: "rgba(247,147,26,0.15)", text: "#f7931a", border: "rgba(247,147,26,0.4)" },
};

const cellEllipsisSx = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  maxWidth: "100%",
  display: "block",
};

const WalletManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientFilterFromUrl = searchParams.get("client") || "";

  const [isLoading, setIsLoading] = useState(true);
  const [walletRows, setWalletRows] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 400);
  const [assetFilter, setAssetFilter] = useState("");
  const [networkFilter, setNetworkFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [typeFilter, setTypeFilter] = useState("");
  const [securityFilter, setSecurityFilter] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [addOpen, setAddOpen] = useState(false);
  const [addWalletType, setAddWalletType] = useState(WALLET_TYPES.WITHDRAWAL);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [preselectedClient, setPreselectedClient] = useState(null);
  const [revealedAddresses, setRevealedAddresses] = useState({});
  const [missingOpen, setMissingOpen] = useState(false);
  const [warningsOpen, setWarningsOpen] = useState(false);

  const loadWallets = useCallback(async () => {
    setIsLoading(true);
    try {
      const usersSnap = await getDocs(
        query(collection(DATABASE, "users"), where("role", "==", 1))
      );
      const users = usersSnap.docs.map((d) => ({ uid: d.id, ...d.data() }));
      setAllClients(users);

      const rows = [];
      await Promise.all(
        users.map(async (user) => {
          const userId = user.doc_id || user.uid;
          try {
            const walletsSnap = await getDocs(
              collection(DATABASE, "users", userId, "crypto_wallets")
            );
            walletsSnap.docs.forEach((wDoc) => {
              const w = wDoc.data();
              rows.push({
                id: `${userId}_${wDoc.id}`,
                wallet_doc_id: wDoc.id,
                user_id: userId,
                user_type: user.user_type,
                client_name: user.full_name || "--",
                client_email: user.email || "--",
                wallet_id: w.id || wDoc.id,
                asset: w.asset || "USDT",
                network: w.network || "POLYGON",
                address: w.address || "",
                label: w.label || "",
                provider: w.provider || inferWalletProviderFromLabel(w.label),
                wallet_type: w.wallet_type || WALLET_TYPES.WITHDRAWAL,
                is_active: w.is_active !== false,
                is_default: !!w.is_default,
                added_by: w.added_by || "--",
                added_date: w.added_date,
                last_edited_at: w.last_edited_at || null,
                last_edited_by: w.last_edited_by || null,
                auto_pay_eligible_from: w.auto_pay_eligible_from || null,
                edit_suspended_until: w.edit_suspended_until || null,
                edit_history: w.edit_history,
                is_legacy_security: isLegacySecurityWallet({
                  is_active: w.is_active !== false,
                  auto_pay_eligible_from: w.auto_pay_eligible_from,
                  edit_history: w.edit_history,
                }),
              });
            });
          } catch (err) {
            console.warn(`Failed to load wallets for ${userId}`, err);
          }
        })
      );
      setWalletRows(rows);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [clientFilterFromUrl]);

  useEffect(() => { loadWallets(); }, [loadWallets]);

  useEffect(() => {
    if (clientFilterFromUrl) setPreselectedClient({ doc_id: clientFilterFromUrl });
  }, [clientFilterFromUrl]);

  const filteredRows = useMemo(() => {
    let list = [...walletRows];
    if (clientFilterFromUrl) list = list.filter((r) => r.user_id === clientFilterFromUrl);
    if (debouncedSearch) {
      const q = debouncedSearch.toLowerCase();
      list = list.filter(
        (r) =>
          r.client_name?.toLowerCase().includes(q) ||
          r.client_email?.toLowerCase().includes(q) ||
          r.label?.toLowerCase().includes(q) ||
          r.provider?.toLowerCase().includes(q) ||
          r.wallet_id?.toLowerCase().includes(q) ||
          r.address?.toLowerCase().includes(q)
      );
    }
    if (assetFilter)  list = list.filter((r) => r.asset === assetFilter);
    if (networkFilter) list = list.filter((r) => r.network === networkFilter);
    if (statusFilter === "active")   list = list.filter((r) => r.is_active);
    if (statusFilter === "inactive") list = list.filter((r) => !r.is_active);
    if (typeFilter) list = list.filter((r) => r.wallet_type === typeFilter);
    if (securityFilter === "legacy") list = list.filter((r) => r.is_legacy_security);
    if (securityFilter === "ok") list = list.filter((r) => !r.is_legacy_security);
    list.sort((a, b) =>
      (a.client_name || "").localeCompare(b.client_name || "", undefined, { sensitivity: "base" })
    );
    return list.map((r, i) => ({ ...r, rowid: i + 1 }));
  }, [walletRows, clientFilterFromUrl, debouncedSearch, assetFilter, networkFilter, statusFilter, typeFilter, securityFilter]);

  const clientStats = useMemo(() => {
    const activeClients = allClients.filter((c) => c.status === 1);
    const activeIndividual = activeClients.filter((c) => c.user_type === 0);
    const activeCorporate = activeClients.filter((c) => c.user_type === 1);

    const individualIds = new Set(activeIndividual.map((c) => c.doc_id || c.uid));
    const corporateIds = new Set(activeCorporate.map((c) => c.doc_id || c.uid));

    const activeWithdrawal = walletRows.filter(
      (r) => r.wallet_type === WALLET_TYPES.WITHDRAWAL && r.is_active
    );
    const withdrawalIndividual = activeWithdrawal.filter((r) => individualIds.has(r.user_id));
    const withdrawalCorporate = activeWithdrawal.filter((r) => corporateIds.has(r.user_id));

    const individualsWithWithdrawal = new Set(withdrawalIndividual.map((r) => r.user_id));
    const missingIndividualsList = activeIndividual
      .filter((c) => !individualsWithWithdrawal.has(c.doc_id || c.uid))
      .map((c) => ({ id: c.doc_id || c.uid, name: c.full_name || "—", email: c.email || "" }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

    return {
      activeClients: activeClients.length,
      activeIndividual: activeIndividual.length,
      activeCorporate: activeCorporate.length,
      withdrawalIndividual: withdrawalIndividual.length,
      withdrawalCorporate: withdrawalCorporate.length,
      individualsMissing: missingIndividualsList.length,
      missingIndividualsList,
    };
  }, [allClients, walletRows]);

  const walletWarnings = useMemo(() => {
    const clientById = {};
    allClients.forEach((c) => { clientById[c.doc_id || c.uid] = c; });
    const nameOf = (uid) => {
      const c = clientById[uid];
      return c ? (c.full_name || c.email || uid) : uid;
    };

    const active = walletRows.filter((r) => r.is_active);
    const warnings = [];

    // Same address registered to more than one client (or duplicated for one client)
    const byAddr = new Map();
    active.forEach((r) => {
      const key = normalizeAddressForCompare(r.address, r.network);
      if (!key) return;
      if (!byAddr.has(key)) byAddr.set(key, []);
      byAddr.get(key).push(r);
    });
    byAddr.forEach((group) => {
      const clientIds = [...new Set(group.map((r) => r.user_id))];
      if (clientIds.length > 1) {
        warnings.push({
          severity: "high",
          type: "Duplicate across clients",
          message: `Same address registered to ${clientIds.length} different clients`,
          address: group[0].address,
          network: group[0].network,
          clients: clientIds.map((id) => ({ id, name: nameOf(id) })),
        });
      } else if (group.length > 1) {
        warnings.push({
          severity: "medium",
          type: "Duplicate (same client)",
          message: `Registered ${group.length}× for the same client`,
          address: group[0].address,
          network: group[0].network,
          clients: [{ id: group[0].user_id, name: nameOf(group[0].user_id) }],
        });
      }
    });

    // Address that doesn't match its network
    active.forEach((r) => {
      const res = validateWalletAddress(r.address, r.network);
      if (!res.valid) {
        warnings.push({
          severity: "medium",
          type: "Invalid address",
          message: res.message || "Address does not match its network",
          address: r.address,
          network: r.network,
          clients: [{ id: r.user_id, name: nameOf(r.user_id) }],
        });
      }
    });

    const order = { high: 0, medium: 1, low: 2 };
    warnings.sort((a, b) => order[a.severity] - order[b.severity]);
    return warnings;
  }, [walletRows, allClients]);

  const openAddWithdrawalFor = (clientId) => {
    setPreselectedClient({ doc_id: clientId });
    setAddWalletType(WALLET_TYPES.WITHDRAWAL);
    setAddOpen(true);
    setMissingOpen(false);
  };

  const legacyActiveCount = useMemo(
    () => walletRows.filter((r) => r.is_legacy_security).length,
    [walletRows]
  );

  const paginatedRows = useMemo(() => {
    const start = page * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, page, pageSize]);

  const toggleReveal = (key) =>
    setRevealedAddresses((prev) => ({ ...prev, [key]: !prev[key] }));

  const columns = [
    {
      field: "client",
      headerName: "Client",
      width: 200,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const name = params.row.client_name || "—";
        const email = params.row.client_email || "";
        return (
          <Tooltip title={email ? `${name}\n${email}` : name}>
            <Box sx={{ lineHeight: 1.35, minWidth: 0, width: "100%", textAlign: "left", py: 0.25 }}>
              <Typography
                variant="body2"
                sx={{ ...cellEllipsisSx, fontWeight: 600, fontSize: 14, color: "text.primary" }}
              >
                {name}
              </Typography>
              {email ? (
                <Typography
                  variant="caption"
                  sx={{ ...cellEllipsisSx, fontSize: 12, color: "text.secondary", display: "block" }}
                >
                  {email}
                </Typography>
              ) : null}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "label",
      headerName: "Label",
      flex: 1,
      minWidth: 140,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "leftAlign",
      headerAlign: "left",
      renderCell: (params) => {
        const labelText = (params.row.label || "").trim() || "—";
        return (
          <Tooltip title={labelText}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                fontSize: 13,
                color: "text.primary",
                whiteSpace: "normal",
                wordBreak: "break-word",
                lineHeight: 1.3,
                py: 0.25,
              }}
            >
              {labelText}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "wallet_id",
      headerName: "Wallet ID",
      width: 88,
      hideOnMobile: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Typography
          variant="caption"
          sx={{ fontFamily: "monospace", fontSize: 11, color: "text.secondary" }}
        >
          {params.row.wallet_id || "—"}
        </Typography>
      ),
    },
    {
      field: "asset",
      headerName: "Asset",
      width: 72,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const asset = params.row.asset || "USDT";
        const colors = ASSET_COLORS[asset] || { bg: "rgba(255,255,255,0.08)", text: "rgba(255,255,255,0.7)", border: "rgba(255,255,255,0.2)" };
        return (
          <Chip
            size="small"
            label={asset}
            sx={{
              height: 22,
              fontWeight: 700,
              fontSize: 11,
              bgcolor: colors.bg,
              color: colors.text,
              border: `1px solid ${colors.border}`,
            }}
          />
        );
      },
    },
    {
      field: "network",
      headerName: "Network",
      width: 100,
      hideOnTablet: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const net = params.row.network || "POLYGON";
        const nc = NETWORK_COLORS[net] || { bg: "rgba(255,255,255,0.08)", text: "rgba(255,255,255,0.7)", border: "rgba(255,255,255,0.2)" };
        return (
          <Chip
            size="small"
            label={net}
            sx={{
              height: 22,
              fontWeight: 600,
              fontSize: 10,
              bgcolor: nc.bg,
              color: nc.text,
              border: `1px solid ${nc.border}`,
            }}
          />
        );
      },
    },
    {
      field: "provider",
      headerName: "Provider",
      width: 128,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const provider = params.row.provider || "";
        if (!provider) {
          return (
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              —
            </Typography>
          );
        }
        const pc = PROVIDER_COLORS[provider] || {
          bg: "rgba(255,255,255,0.08)",
          text: "rgba(255,255,255,0.85)",
          border: "rgba(255,255,255,0.2)",
        };
        return (
          <Chip
            size="small"
            label={provider}
            sx={{
              height: 22,
              fontWeight: 600,
              fontSize: 10,
              bgcolor: pc.bg,
              color: pc.text,
              border: `1px solid ${pc.border}`,
            }}
          />
        );
      },
    },
    {
      field: "address",
      headerName: "Address",
      width: 156,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "leftAlign",
      renderCell: (params) => {
        const key = `${params.row.user_id}_${params.row.wallet_doc_id}`;
        const revealed = revealedAddresses[key];
        const display = revealed
          ? params.row.address
          : maskAddress(params.row.address, params.row.network);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, minWidth: 0, maxWidth: "100%" }}>
            <Tooltip title={params.row.address || display}>
              <Typography
                component="span"
                sx={{ fontFamily: "monospace", fontSize: 12, minWidth: 0, maxWidth: 120, ...cellEllipsisSx }}
              >
                {display}
              </Typography>
            </Tooltip>
            <IconButton size="small" onClick={() => toggleReveal(key)} sx={{ flexShrink: 0, p: 0.25 }}>
              {revealed ? (
                <VisibilityOffIcon sx={{ fontSize: 16 }} />
              ) : (
                <VisibilityIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Box>
        );
      },
    },
    {
      field: "wallet_type",
      headerName: "Type",
      width: 104,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const wt = params.row.wallet_type || WALLET_TYPES.WITHDRAWAL;
        const isWithdrawal = wt === WALLET_TYPES.WITHDRAWAL;
        return (
          <Chip
            size="small"
            label={isWithdrawal ? "Withdrawal" : "Deposit"}
            sx={{
              height: 22,
              fontSize: 10,
              fontWeight: 600,
              bgcolor: isWithdrawal ? "rgba(103,58,183,0.18)" : "rgba(42,245,152,0.15)",
              color: isWithdrawal ? "#b39ddb" : "#2af598",
              border: `1px solid ${isWithdrawal ? "#7c4dff55" : "#2af59855"}`,
            }}
          />
        );
      },
    },
    {
      field: "is_legacy_security",
      headerName: "Security",
      width: 88,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        if (!params.row.is_legacy_security) {
          return (
            <Typography variant="caption" sx={{ fontSize: 11, color: "text.secondary" }}>
              OK
            </Typography>
          );
        }
        return (
          <Tooltip title="Pre-security wallet: review in docs/active/WALLET_LEGACY_SECURITY_REVIEW.md — edit metadata or deactivate">
            <Chip
              size="small"
              label="Legacy"
              sx={{
                height: 20,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: "rgba(255,152,0,0.15)",
                color: "#ffb74d",
                border: "1px solid rgba(255,152,0,0.45)",
              }}
            />
          </Tooltip>
        );
      },
    },
    {
      field: "is_active",
      headerName: "Status",
      width: 80,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.25 }}>
          <Chip
            size="small"
            label={params.row.is_active ? "Active" : "Inactive"}
            color={params.row.is_active ? "success" : "default"}
            sx={{ height: 20, fontSize: 10, "& .MuiChip-label": { px: 0.75 } }}
          />
          {params.row.is_default && params.row.is_active && (
            <Typography variant="caption" sx={{ fontSize: 9, color: "primary.light", fontWeight: 700, lineHeight: 1 }}>
              DEFAULT
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: "last_edited_at",
      headerName: "Edited",
      width: 76,
      hideOnMobile: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const d = params.row.last_edited_at;
        if (!d) {
          return (
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              —
            </Typography>
          );
        }
        const m = d?.toDate ? moment(d.toDate()) : moment(d.seconds * 1000);
        return (
          <Tooltip title={`Edited by ${params.row.last_edited_by || "—"}`}>
            <Typography variant="caption" sx={{ fontSize: 12, color: "text.primary", fontWeight: 500 }}>
              {m.format("DD/MM/YY")}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: "added_by",
      headerName: "Added By",
      width: 100,
      hideOnTablet: true,
      sortable: false,
      disableColumnMenu: true,
      cellClassName: "leftAlign",
      renderCell: (params) => (
        <Typography variant="caption" sx={{ ...cellEllipsisSx, fontSize: 11, color: "text.secondary" }}>
          {params.row.added_by || "—"}
        </Typography>
      ),
    },
    {
      field: "added_date",
      headerName: "Added",
      width: 72,
      hideOnMobile: true,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const d = params.row.added_date;
        if (!d) {
          return (
            <Typography variant="body2" sx={{ fontSize: 12, color: "text.secondary" }}>
              —
            </Typography>
          );
        }
        const m = d?.toDate ? moment(d.toDate()) : moment(d.seconds * 1000);
        return (
          <Typography variant="caption" sx={{ fontSize: 12, color: "text.primary", fontWeight: 500 }}>
            {m.format("DD/MM/YY")}
          </Typography>
        );
      },
    },
    {
      field: "actions",
      headerName: "Edit",
      width: 52,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Tooltip title="Edit wallet">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedWallet(params.row);
              setEditOpen(true);
            }}
          >
            <EditIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 } }}>

      {/* ── Header ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          mb: 2.5,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "text.primary", lineHeight: 1.2 }}>
            Wallet Management
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "rgba(255,255,255,0.72)", mt: 0.75, maxWidth: 640, lineHeight: 1.5 }}
          >
            <Box component="span" sx={{ color: "#ff8a80", fontWeight: 600 }}>Withdrawal</Box>
            {" "}— client payout addresses (max {MAX_ACTIVE_WALLETS_PER_USER} active).{" "}
            <Box component="span" sx={{ color: "#2af598", fontWeight: 600 }}>Deposit</Box>
            {" "}— Fiddux receiving addresses per client (app not live yet).
          </Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", mt: 0.5 }}>
            Auto-pay: 24h cooldown on new/edited wallets · thresholds &amp; timing in Admin Settings → Automations
            {legacyActiveCount > 0 && (
              <>
                {" · "}
                <Box component="span" sx={{ color: "#ffb74d", fontWeight: 600 }}>
                  {legacyActiveCount} legacy wallet{legacyActiveCount !== 1 ? "s" : ""} need review
                </Box>
              </>
            )}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          {clientFilterFromUrl && (
            <Button
              variant="outlined"
              onClick={() => navigate("/admin/kyc-control-center")}
              sx={{
                borderColor: "rgba(126,184,255,0.5)",
                color: "#7eb8ff",
                fontWeight: 600,
              }}
            >
              KYC Center
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setPreselectedClient(clientFilterFromUrl ? { doc_id: clientFilterFromUrl } : null);
              setAddWalletType(WALLET_TYPES.DEPOSIT);
              setAddOpen(true);
            }}
            sx={{
              borderColor: "rgba(42,245,152,0.4)",
              color: "#2af598",
              fontWeight: 700,
              "&:hover": { borderColor: "#2af598", bgcolor: "rgba(42,245,152,0.08)" },
            }}
          >
            Deposit Wallet
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setPreselectedClient(clientFilterFromUrl ? { doc_id: clientFilterFromUrl } : null);
              setAddWalletType(WALLET_TYPES.WITHDRAWAL);
              setAddOpen(true);
            }}
            sx={{
              background: "linear-gradient(135deg, #c62828 0%, #ef5350 100%)",
              color: "#fff",
              fontWeight: 700,
              "&:hover": { background: "linear-gradient(135deg, #e53935 0%, #ff6659 100%)" },
            }}
          >
            Withdrawal Wallet
          </Button>
        </Box>
      </Box>

      {/* ── Overview stats (desktop only) ── */}
      {!isLoading && (
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            flexWrap: "wrap",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          {[
            { label: "Active clients", value: clientStats.activeClients, color: "#fff", hint: "Approved clients" },
            { label: "Active — Individual", value: clientStats.activeIndividual, color: "#7eb8ff", hint: "Approved individual clients" },
            { label: "Active — Corporate", value: clientStats.activeCorporate, color: "#ffb74d", hint: "Approved corporate clients" },
            { label: "Withdrawal wallets — Individual", value: clientStats.withdrawalIndividual, color: "#2af598", hint: "Active withdrawal wallets on individual clients" },
            { label: "Withdrawal wallets — Corporate", value: clientStats.withdrawalCorporate, color: "#2af598", hint: "Active withdrawal wallets on corporate clients" },
            {
              label: "Individuals missing withdrawal wallet",
              value: clientStats.individualsMissing,
              color: clientStats.individualsMissing > 0 ? "#ff8a80" : "#2af598",
              hint: "Click to see which individual clients have no active withdrawal wallet (each should have at least 1)",
              highlight: clientStats.individualsMissing > 0,
              onClick: clientStats.individualsMissing > 0 ? () => setMissingOpen(true) : undefined,
            },
            {
              label: "Wallet warnings",
              value: walletWarnings.length,
              color: walletWarnings.length > 0 ? "#ffb74d" : "#2af598",
              hint: "Click to review possible issues: invalid addresses, duplicates across clients, etc.",
              highlight: walletWarnings.length > 0,
              isWarning: true,
              onClick: () => setWarningsOpen(true),
            },
          ].map((s) => (
            <Tooltip key={s.label} title={s.hint}>
              <Box
                onClick={s.onClick}
                sx={{
                  flex: "1 1 160px",
                  minWidth: 150,
                  p: 1.75,
                  borderRadius: 2,
                  position: "relative",
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: s.highlight
                    ? `1px solid ${s.isWarning ? "rgba(255,183,77,0.5)" : "rgba(255,138,128,0.5)"}`
                    : "1px solid rgba(255,255,255,0.08)",
                  cursor: s.onClick ? "pointer" : "default",
                  transition: "background-color 0.15s, border-color 0.15s",
                  "&:hover": s.onClick ? { bgcolor: "rgba(255,255,255,0.07)", borderColor: "rgba(255,255,255,0.25)" } : {},
                }}
              >
                {s.isWarning && (
                  <Box sx={{ position: "absolute", top: 10, right: 12 }}>
                    {walletWarnings.length > 0
                      ? <WarningAmberIcon sx={{ fontSize: 18, color: "#ffb74d" }} />
                      : <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "#2af598" }} />}
                  </Box>
                )}
                <Typography sx={{ fontSize: 30, fontWeight: 800, color: s.color, lineHeight: 1.1 }}>
                  {s.value}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.65)", display: "block", mt: 0.25, lineHeight: 1.3 }}>
                  {s.label}
                </Typography>
                {s.onClick && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.4)", display: "block", mt: 0.5, fontSize: 10 }}>
                    Click to view
                  </Typography>
                )}
              </Box>
            </Tooltip>
          ))}
        </Box>
      )}

      {/* ── Filters ── */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1.5,
          mb: 2.5,
          p: 2,
          borderRadius: 2,
          bgcolor: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        {/* Search */}
        <Box sx={{ flex: "1 1 220px", minWidth: 180 }}>
          <CustomSearch
            placeholder="Search client, email, label, address…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            onClear={() => { setSearch(""); setPage(0); }}
          />
        </Box>

        {/* Asset */}
        <FormControl size="small" sx={{ flex: "1 1 120px", minWidth: 110 }}>
          <InputLabel sx={LABEL_SX}>Asset</InputLabel>
          <Select
            value={assetFilter}
            label="Asset"
            onChange={(e) => { setAssetFilter(e.target.value); setPage(0); }}
            sx={FILTER_SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            {assetOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Network */}
        <FormControl size="small" sx={{ flex: "1 1 140px", minWidth: 120 }}>
          <InputLabel sx={LABEL_SX}>Network</InputLabel>
          <Select
            value={networkFilter}
            label="Network"
            onChange={(e) => { setNetworkFilter(e.target.value); setPage(0); }}
            sx={FILTER_SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            {networkOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Status */}
        <FormControl size="small" sx={{ flex: "1 1 130px", minWidth: 110 }}>
          <InputLabel sx={LABEL_SX}>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            sx={FILTER_SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            {statusOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Type */}
        <FormControl size="small" sx={{ flex: "1 1 130px", minWidth: 120 }}>
          <InputLabel sx={LABEL_SX}>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
            sx={FILTER_SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            {typeOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Security (legacy pre auto_pay_eligible_from) */}
        <FormControl size="small" sx={{ flex: "1 1 150px", minWidth: 130 }}>
          <InputLabel sx={LABEL_SX}>Security</InputLabel>
          <Select
            value={securityFilter}
            label="Security"
            onChange={(e) => { setSecurityFilter(e.target.value); setPage(0); }}
            sx={FILTER_SELECT_SX}
            MenuProps={MENU_PROPS}
          >
            {securityOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* ── Results count ── */}
      {!isLoading && (
        <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1 }}>
          {filteredRows.length} wallet{filteredRows.length !== 1 ? "s" : ""} found
          {clientFilterFromUrl ? " · filtered by client" : ""}
        </Typography>
      )}

      {/* ── Table ── */}
      <TableContainer
        columns={columns}
        rows={paginatedRows}
        loading={isLoading}
        page={page}
        pageSize={pageSize}
        totalCount={filteredRows.length}
        handlePageChange={setPage}
        onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
        paginationShow={true}
        rowHeight={52}
        density="standard"
      />

      <AddWalletModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSuccess={loadWallets}
        preselectedClient={preselectedClient}
        initialWalletType={addWalletType}
      />
      <EditWalletModal
        open={editOpen}
        onClose={() => { setEditOpen(false); setSelectedWallet(null); }}
        onSuccess={loadWallets}
        walletRow={selectedWallet}
      />

      {/* ── Individuals missing a withdrawal wallet ── */}
      <Dialog
        open={missingOpen}
        onClose={() => setMissingOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#1a1a2e" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonOffIcon sx={{ color: "#ff8a80" }} />
            <Typography sx={{ fontWeight: 700, color: "#fff" }}>
              Individuals missing a withdrawal wallet
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setMissingOpen(false)} sx={{ color: "rgba(255,255,255,0.6)" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mb: 1.5 }}>
            {clientStats.missingIndividualsList?.length || 0} active individual client
            {clientStats.missingIndividualsList?.length !== 1 ? "s" : ""} without any active withdrawal wallet.
          </Typography>
          {(clientStats.missingIndividualsList || []).length === 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2af598" }} />
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>All individual clients have a withdrawal wallet.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {(clientStats.missingIndividualsList || []).map((c) => (
                <Box
                  key={c.id}
                  sx={{
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
                    p: 1.25, borderRadius: 1.5, border: "1px solid rgba(255,255,255,0.1)", bgcolor: "rgba(0,0,0,0.2)",
                  }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ color: "#fff", fontWeight: 600, ...cellEllipsisSx }}>
                      {c.name}
                    </Typography>
                    {c.email && (
                      <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", ...cellEllipsisSx }}>
                        {c.email}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                    onClick={() => openAddWithdrawalFor(c.id)}
                    sx={{ flexShrink: 0, color: "#ff8a80", borderColor: "rgba(255,138,128,0.4)", fontWeight: 700, "&:hover": { borderColor: "#ff8a80", bgcolor: "rgba(255,138,128,0.08)" } }}
                  >
                    Add withdrawal
                  </Button>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Wallet warnings ── */}
      <Dialog
        open={warningsOpen}
        onClose={() => setWarningsOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { bgcolor: "#1a1a2e" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon sx={{ color: "#ffb74d" }} />
            <Typography sx={{ fontWeight: 700, color: "#fff" }}>
              Wallet warnings
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setWarningsOpen(false)} sx={{ color: "rgba(255,255,255,0.6)" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)", display: "block", mb: 1.5 }}>
            Checks active wallets for invalid addresses, duplicates across clients, and same-client duplicates.
          </Typography>
          {walletWarnings.length === 0 ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CheckCircleOutlineIcon sx={{ color: "#2af598" }} />
              <Typography sx={{ color: "rgba(255,255,255,0.8)" }}>No issues found in active wallets.</Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {walletWarnings.map((w, i) => {
                const isHigh = w.severity === "high";
                const accent = isHigh ? "#ff8a80" : "#ffb74d";
                return (
                  <Box
                    key={`${w.type}-${i}`}
                    sx={{
                      p: 1.5, borderRadius: 1.5,
                      border: `1px solid ${isHigh ? "rgba(255,138,128,0.45)" : "rgba(255,183,77,0.4)"}`,
                      bgcolor: isHigh ? "rgba(255,138,128,0.08)" : "rgba(255,183,77,0.06)",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                      {isHigh ? <ErrorOutlineIcon sx={{ fontSize: 18, color: accent }} /> : <WarningAmberIcon sx={{ fontSize: 18, color: accent }} />}
                      <Typography variant="body2" sx={{ color: accent, fontWeight: 700 }}>
                        {w.type}
                      </Typography>
                      <Chip size="small" label={w.severity.toUpperCase()} sx={{ height: 18, fontSize: 9, fontWeight: 700, color: accent, bgcolor: "transparent", border: `1px solid ${accent}66` }} />
                      <Chip size="small" label={w.network} variant="outlined" sx={{ height: 18, fontSize: 9, color: "rgba(255,255,255,0.7)", borderColor: "rgba(255,255,255,0.2)" }} />
                    </Box>
                    <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.8)", display: "block" }}>
                      {w.message}
                    </Typography>
                    <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "#fff", wordBreak: "break-all", mt: 0.5 }}>
                      {w.address}
                    </Typography>
                    <Divider sx={{ borderColor: "rgba(255,255,255,0.08)", my: 0.75 }} />
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {w.clients.map((c) => (
                        <Chip
                          key={c.id}
                          size="small"
                          label={c.name}
                          onClick={() => { setWarningsOpen(false); navigate(`/admin/wallet-management?client=${c.id}`); }}
                          sx={{ height: 22, fontSize: 11, color: "#fff", bgcolor: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)", cursor: "pointer", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
                        />
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default WalletManagement;
